import { Tag, TagLabel, TagLeftIcon, TagProps, TagCloseButton } from "@chakra-ui/react";
import { sizeMap } from "helpers/tag";
import Linkify from "components/DataDisplay/Linkify";
import { v2pathPathById, v2prebuiltPathPathById } from "helpers/url/pages/v2/pathPath";
import { useV2PathDetailQuery } from "features/Path/hooks";
import { usePrebuiltPathDetailQuery } from "features/Prebuilt/PrebuiltPath/hooks";
import { iconColorPaletteFor, iconComponentFor } from "features/Path/icons";
import SkeletonTag from "./SkeltonTag";

export interface BaseNameTagProps extends TagProps {
  pathId: string | undefined;
  pathName: string | undefined | null;
  icon?: string | null;
  iconColor?: string | null;
  includeLink?: boolean;
  onRemove?: () => void;
  tagLabelProps?: object;
  size?: "sm" | "md";
  removeable?: boolean;
  isPrebuilt?: boolean;
}

export default function BasePathTag({
  pathId,
  pathName,
  icon,
  iconColor,
  onRemove = () => {},
  isPrebuilt = false,
  includeLink = true,
  tagLabelProps = {},
  size = "sm",
  removeable = false,
  ...props
}: BaseNameTagProps) {
  if (!pathName) {
    return <SkeletonTag />;
  }
  const { tagSizeProps, tagLabelSizeProps } = sizeMap(size);

  const url = includeLink && pathId ? isPrebuilt ? v2prebuiltPathPathById(pathId) : v2pathPathById(pathId) : "";
  const IconComponent = iconComponentFor(icon);
  const iconFg = iconColorPaletteFor(iconColor).fg;
  return (
    <Tag colorScheme="gray" maxWidth="none" {...tagSizeProps} {...props}>
      <TagLeftIcon as={IconComponent} color={iconFg} />
      <TagLabel
        fontWeight="normal"
        {...tagLabelSizeProps}
        {...tagLabelProps}
      >
        <Linkify linkTo={url} _hover={{ textDecoration: "underline" }}>
          {pathName}
        </Linkify>
      </TagLabel>
      {removeable && <TagCloseButton onClick={onRemove} />}
    </Tag>
  );
}

type NameTagWithoutPathName = Omit<BaseNameTagProps, "pathName">;

export function PathTag({
  pathId, onRemove = () => {}, includeLink = true, tagLabelProps = {}, size = "md", removeable = false,
}: NameTagWithoutPathName) {
  const { data: path } = useV2PathDetailQuery(pathId);
  return (
    <BasePathTag
      pathId={pathId}
      pathName={path?.name}
      icon={path?.icon}
      iconColor={path?.iconColor}
      onRemove={onRemove}
      includeLink={includeLink}
      tagLabelProps={tagLabelProps}
      size={size}
      removeable={removeable}
    />
  );
}

export function PrebuiltPathTag({
  pathId, onRemove = () => {}, includeLink = true, tagLabelProps = {}, size = "md", removeable = false,
}: NameTagWithoutPathName) {
  const { data: path } = usePrebuiltPathDetailQuery(pathId);
  return (
    <BasePathTag
      pathId={pathId}
      pathName={path?.name}
      onRemove={onRemove}
      includeLink={includeLink}
      tagLabelProps={tagLabelProps}
      size={size}
      removeable={removeable}
    />
  );
}
